import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      search: 'Rechercher',
      about: 'À propos',
      language: 'Langue'
    },
    // Hero Section
    hero: {
      title: 'Découvrez Votre Saint Patron',
      subtitle: 'Entrez votre prénom et découvrez le saint qui veille sur vous, son archange gardien et une prière personnalisée pour invoquer sa protection divine.',
      placeholder: 'Entrez votre prénom...',
      button: 'Découvrir Mon Saint',
      searching: 'Recherche en cours...'
    },
    // Results Section
    results: {
      yourSaint: 'Votre Saint Patron',
      feastDay: 'Jour de Fête',
      archangel: 'Archange Gardien',
      prayer: 'Prière Personnalisée',
      history: 'Histoire du Saint',
      noResults: 'Aucun saint trouvé pour ce prénom',
      tryAnother: 'Essayez un autre prénom',
      generating: 'Génération de l\'image sacrée...',
      generatingPrayer: 'Composition de votre prière...'
    },
    // Features Section
    features: {
      title: 'Une Expérience Spirituelle Unique',
      subtitle: 'Notre application combine tradition religieuse et technologie moderne pour vous offrir une connexion profonde avec votre saint patron.',
      saintImage: {
        title: 'Image du Saint',
        description: 'Une représentation artistique de votre saint patron dans le style classique de la Renaissance.'
      },
      archangelImage: {
        title: 'Archange Gardien',
        description: 'Découvrez l\'archange associé à votre saint et sa signification spirituelle.'
      },
      personalPrayer: {
        title: 'Prière Personnalisée',
        description: 'Une prière unique générée spécialement pour invoquer la protection de votre saint.'
      },
      history: {
        title: 'Histoire & Origine',
        description: 'Apprenez l\'histoire fascinante de votre saint et l\'origine de votre prénom.'
      }
    },
    // Footer
    footer: {
      description: 'Connectez-vous à votre héritage spirituel et découvrez la signification sacrée de votre prénom.',
      rights: 'Tous droits réservés',
      madeWith: 'Fait avec',
      faith: 'foi'
    },
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      retry: 'Réessayer',
      close: 'Fermer',
      share: 'Partager',
      download: 'Télécharger'
    }
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      search: 'Search',
      about: 'About',
      language: 'Language'
    },
    // Hero Section
    hero: {
      title: 'Discover Your Patron Saint',
      subtitle: 'Enter your first name and discover the saint who watches over you, their guardian archangel, and a personalized prayer to invoke their divine protection.',
      placeholder: 'Enter your first name...',
      button: 'Discover My Saint',
      searching: 'Searching...'
    },
    // Results Section
    results: {
      yourSaint: 'Your Patron Saint',
      feastDay: 'Feast Day',
      archangel: 'Guardian Archangel',
      prayer: 'Personalized Prayer',
      history: 'Saint\'s History',
      noResults: 'No saint found for this name',
      tryAnother: 'Try another name',
      generating: 'Generating sacred image...',
      generatingPrayer: 'Composing your prayer...'
    },
    // Features Section
    features: {
      title: 'A Unique Spiritual Experience',
      subtitle: 'Our application combines religious tradition and modern technology to offer you a deep connection with your patron saint.',
      saintImage: {
        title: 'Saint Image',
        description: 'An artistic representation of your patron saint in the classic Renaissance style.'
      },
      archangelImage: {
        title: 'Guardian Archangel',
        description: 'Discover the archangel associated with your saint and their spiritual significance.'
      },
      personalPrayer: {
        title: 'Personalized Prayer',
        description: 'A unique prayer specially generated to invoke the protection of your saint.'
      },
      history: {
        title: 'History & Origin',
        description: 'Learn the fascinating history of your saint and the origin of your first name.'
      }
    },
    // Footer
    footer: {
      description: 'Connect to your spiritual heritage and discover the sacred meaning of your first name.',
      rights: 'All rights reserved',
      madeWith: 'Made with',
      faith: 'faith'
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      retry: 'Retry',
      close: 'Close',
      share: 'Share',
      download: 'Download'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  }, []);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isFrench: language === 'fr',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
